import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import MeridianLoader from '@amzn/meridian/loader';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import { useTranslation } from 'src/arb-wrapper';
import { FormInput } from 'src/components/common/forms';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { PublisherAccountFields, buildPublisherAccountConfig } from 'src/components/publisher-config/PublisherAccountFields';
import { Loader } from 'src/components/common/Loader';
import {
  useCampaignConfigurationControllerGetQuery,
  useCampaignConfigurationControllerUpdateMutation,
  useCampaignConfigurationControllerDeleteMutation,
  type PublisherAccountConfigValue,
} from 'src/store/leela-api';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { DeleteConfirmModal } from 'src/components/common/DeleteConfirmModal';
import { BusinessLines, Countries } from 'src/store/constants';
import type { Country, BusinessLine } from 'src/store/generated/leela-api-generated';

export const PublisherConfigDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createToast } = useToasterContext();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: config, isLoading } = useCampaignConfigurationControllerGetQuery({ id: id! }, { skip: !id });
  const [updateConfig, { isLoading: isSaving }] = useCampaignConfigurationControllerUpdateMutation();
  const [deleteConfig, { isLoading: isDeleting }] = useCampaignConfigurationControllerDeleteMutation();

  const configValue = config?.config as PublisherAccountConfigValue | undefined;
  const configFields = configValue as Record<string, unknown> | undefined;
  const publisher = configValue?.publisher;

  const formValues: Record<string, unknown> | undefined = config
    ? {
        name: config.name ?? '',
        countryMode: (config.supportedCountries ?? []).length > 0 ? 'specific' : 'all',
        supportedCountries: config.supportedCountries ?? [],
        businessLineMode: (config.supportedBusinessLines ?? []).length > 0 ? 'specific' : 'all',
        supportedBusinessLines: config.supportedBusinessLines ?? [],
        catalogId: configFields?.catalogId,
        businessCenterId: configFields?.businessCenterId,
        advertiserId: configFields?.advertiserId,
        userId: configFields?.userId,
        mccAccountId: configFields?.mccAccountId,
        customerAccountId: configFields?.customerAccountId,
        timezone: configFields?.timezone,
        brandType: configFields?.brandType,
      }
    : undefined;

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    values: formValues,
    mode: 'onTouched',
  });

  const countryMode = watch('countryMode') as string;
  const businessLineMode = watch('businessLineMode') as string;

  const onSave = async (formData: Record<string, unknown>) => {
    try {
      const updatedConfig = buildPublisherAccountConfig(publisher!, formData);

      await updateConfig({
        id: id!,
        updateCampaignConfigurationDto: {
          name: formData.name as string,
          supportedCountries: formData.countryMode === 'all' ? [] as Country[] : formData.supportedCountries as Country[],
          supportedBusinessLines: formData.businessLineMode === 'all' ? [] as BusinessLine[] : formData.supportedBusinessLines as BusinessLine[],
          config: updatedConfig,
        },
      }).unwrap();

      createToast({ type: AlertType.SUCCESS, message: t('publisher_config_update_success') });
      setIsEditing(false);
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } };
      createToast({
        type: AlertType.ERROR,
        message: err?.data?.message ?? t('publisher_config_update_error'),
      });
    }
  };

  const onDelete = async () => {
    try {
      await deleteConfig({ id: id! }).unwrap();
      createToast({ type: AlertType.SUCCESS, message: t('publisher_config_delete_success') });
      navigate('/publisher-configs');
    } catch {
      createToast({ type: AlertType.ERROR, message: t('publisher_config_delete_error') });
    }
  };

  if (isLoading) return <Loader isLoading><div /></Loader>;
  if (!config) return <Text>{t('publisher_config_not_found')}</Text>;

  if (!isEditing) {
    return (
      <Column>
        <BreadcrumbGroup>
          <Breadcrumb onClick={() => { navigate('/publisher-configs'); }}>{t('publisher_configs')}</Breadcrumb>
          <Breadcrumb>{config.name}</Breadcrumb>
        </BreadcrumbGroup>
        <Row alignmentHorizontal="justify">
          <Heading level={1}>{config.name}</Heading>
          <Row>
            <Button type="tertiary" onClick={() => { setShowDeleteModal(true); }}>{t('form_global_delete')}</Button>
            <Button type="primary" onClick={() => { setIsEditing(true); }}>{t('form_global_edit')}</Button>
          </Row>
        </Row>

        <Box spacingInset="400" type="outline">
          <Column spacing="300">
            <Row widths={['fill', 'fill']}>
              <Column spacing="100">
                <Text color="secondary" type="b200">{t('form_campaigns_publisher')}</Text>
                <Text>{publisher}</Text>
              </Column>
              <Column spacing="100">
                <Text color="secondary" type="b200">{t('program_owner')}</Text>
                <Text>{config.owner}</Text>
              </Column>
            </Row>

            <Row widths={['fill', 'fill']}>
              <Column spacing="100">
                <Text color="secondary" type="b200">{t('publisher_config_countries')}</Text>
                <Row spacing="100" wrap="down">
                  {(config.supportedCountries ?? []).length > 0
                    ? (config.supportedCountries ?? []).map(c => <Tag key={c} type="theme">{c}</Tag>)
                    : <Text>{t('all')}</Text>}
                </Row>
              </Column>
              <Column spacing="100">
                <Text color="secondary" type="b200">{t('publisher_config_business_lines')}</Text>
                <Text>{(config.supportedBusinessLines ?? []).length > 0
                  ? (config.supportedBusinessLines ?? []).join(', ')
                  : t('all')}</Text>
              </Column>
            </Row>

            {/* Publisher-specific fields */}
            {publisher && <PublisherAccountFields publisher={publisher} values={configFields} />}
          </Column>
        </Box>

        <DeleteConfirmModal
          isDeleting={isDeleting}
          message={t('publisher_config_delete_confirm_message')}
          open={showDeleteModal}
          title={t('publisher_config_delete_confirm_title')}
          onCancel={() => { setShowDeleteModal(false); }}
          onConfirm={onDelete}
        />
      </Column>
    );
  }

  // Edit mode
  return (
    <Column>
      <BreadcrumbGroup>
        <Breadcrumb onClick={() => { navigate('/publisher-configs'); }}>{t('publisher_configs')}</Breadcrumb>
        <Breadcrumb onClick={() => { setIsEditing(false); }}>{config?.name}</Breadcrumb>
        <Breadcrumb>{t('form_global_edit')}</Breadcrumb>
      </BreadcrumbGroup>
      <Box spacingInset="400" type="outline">
        <Column spacing="300">
          <FormInput
            control={control}
            data-testid="publisher-config-name"
            label={t('form_global_name')}
            name="name"
            required
          />

          <Row widths={['fill', 'fill']}>
            <Column spacing="100">
              <Text color="secondary" type="b200">{t('form_campaigns_publisher')}</Text>
              <Text>{publisher}</Text>
            </Column>
            <div />
          </Row>

          <Row widths={['fill', 'fill']}>
            <Row widths={['fill', 'fill']}>
              <FormSelect
                control={control}
                data-testid="publisher-config-country-mode"
                label={t('publisher_config_countries')}
                name="countryMode"
                options={[{ label: t('all'), value: 'all' }, { label: t('publisher_config_specific'), value: 'specific' }]}
              />
              {countryMode === 'specific'
                ? (
                <FormSelect
                  control={control}
                  data-testid="publisher-config-countries"
                  label={t('publisher_config_select_countries')}
                  name="supportedCountries"
                  options={Countries.map(c => ({ label: c, value: c }))}
                />
                  )
                : <div />}
            </Row>
            <Row widths={['fill', 'fill']}>
              <FormSelect
                control={control}
                data-testid="publisher-config-business-line-mode"
                label={t('publisher_config_business_lines')}
                name="businessLineMode"
                options={[{ label: t('all'), value: 'all' }, { label: t('publisher_config_specific'), value: 'specific' }]}
              />
              {businessLineMode === 'specific'
                ? (
                <FormSelect
                  control={control}
                  data-testid="publisher-config-business-lines"
                  label={t('publisher_config_select_business_lines')}
                  name="supportedBusinessLines"
                  options={Object.values(BusinessLines).map(bl => ({ label: bl, value: bl }))}
                />
                  )
                : <div />}
            </Row>
          </Row>

          {publisher && <PublisherAccountFields control={control} publisher={publisher} setValue={setValue} watch={watch} />}

          <Row alignmentHorizontal="end">
            <Button type="tertiary" onClick={() => { reset(); setIsEditing(false); }}>
              {t('form_global_cancel')}
            </Button>
            <Button disabled={isSaving} type="primary" onClick={handleSubmit(onSave)}>
              {isSaving ? <><MeridianLoader size="small" /> {t('publisher_config_saving')}</> : t('form_global_save')}
            </Button>
          </Row>
        </Column>
      </Box>
    </Column>
  );
};
